import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { openModal } from "../../../app/common/modals/modalSlice";
import {
  functionGetStrategyEntitlement,
  functionGetStrategyGenerationResult,
} from "../../../app/firestore/functionsService";
import { convertAddressFull } from "../../../app/common/util/util";
import TransactionStrategyPropertyGroups from "./TransactionStrategyPropertyGroups";
import BuyerStrategyPropertyRows from "./BuyerStrategyPropertyRows";
import StrategyPageHeader from "./strategyComponents/StrategyPageHeader";
import StrategyPropertyDashboard from "./strategyComponents/StrategyPropertyDashboard";
import StrategyCard from "./strategyComponents/StrategyCard";
import { PricingResultPreview, ListingDescriptionResultPreview, truncate } from "./strategyComponents/StrategyResultPreview";
import { STRATEGY_THEME } from "./strategyComponents/strategyCardTheme";
import { isStrategyVisible } from "./strategyComponents/strategyVisibility";
import { buildPropertyIntelligenceCard } from "./propertyIntelligence/piModel";

// Intelligent Strategies' pricing/comp logic is tuned for Residential
// comps/valuation - Land, Foreclosure, and Commercial need different data
// and methodology that doesn't exist yet, so every Strategy is disabled for
// those property types (set in Edit Transaction's Property Type field) until
// that support is built.
const STRATEGY_UNSUPPORTED_PROPERTY_TYPES = ["Land", "Foreclosure", "Commercial"];
const UNSUPPORTED_PROPERTY_TYPE_MESSAGE =
  "Intelligent Strategies currently support Residential properties only. Land, Foreclosure, and Commercial support is coming soon.";


export default function TransactionStrategyDashboard() {
  const dispatch = useDispatch();
  const { transaction } = useSelector((state) => state.transaction);
  const [existingPricingResult, setExistingPricingResult] = useState(null);
  const [existingListingDescriptionResult, setExistingListingDescriptionResult] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [isLoadingEntitlement, setIsLoadingEntitlement] = useState(false);
  // Bumped on every successful generation so TransactionStrategyPropertyGroups
  // (which otherwise only fetches once on mount) re-fetches and picks up a
  // newly-strategized property without needing a page reload/revisit.
  const [propertyGroupsRefreshSignal, setPropertyGroupsRefreshSignal] = useState(0);
  // Bumped to re-fetch the entitlement (e.g. after Property Intelligence
  // research starts or finishes) without a page reload.
  const [entitlementRefresh, setEntitlementRefresh] = useState(0);

  const isBuyer = transaction?.agentRepresents === "Buyer";
  const isUnsupportedPropertyType = STRATEGY_UNSUPPORTED_PROPERTY_TYPES.includes(transaction?.propertyType);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  // Both derived from the SAME server-verified entitlement call (Admin SDK,
  // never subject to client Firestore rules) rather than a separate direct
  // Firestore read - that separate read is what previously made the button
  // silently forget an already-generated Strategy (e.g. if rules weren't in
  // sync) after navigating away and back.
  const pricingStrategyEntry = entitlement?.existingStrategies?.pricing;
  const pricingSummary = pricingStrategyEntry?.summary;
  const pricingGeneratedAt = pricingStrategyEntry?.generatedAt;
  const hasPricingStrategy = Boolean(pricingStrategyEntry) || Boolean(entitlement?.isPropertyUnlocked);

  const listingDescriptionStrategyEntry = entitlement?.existingStrategies?.listingDescription;
  const listingDescriptionSummary = listingDescriptionStrategyEntry?.summary;
  const listingDescriptionGeneratedAt = listingDescriptionStrategyEntry?.generatedAt;
  const hasListingDescriptionStrategy = Boolean(listingDescriptionStrategyEntry);

  const addressKey = JSON.stringify(transaction?.address || {});

  useEffect(() => {
    let cancelled = false;

    if (transaction?.id) {
      setIsLoadingEntitlement(true);
      functionGetStrategyEntitlement({ transactionId: transaction.id })
        .then((data) => {
          if (cancelled) return;
          setEntitlement(data);

          const pricingGenerationId = data?.existingStrategies?.pricing?.generationId;
          if (pricingGenerationId) {
            functionGetStrategyGenerationResult({ transactionId: transaction.id, generationId: pricingGenerationId })
              .then((generation) => {
                if (cancelled || !generation) return;
                setExistingPricingResult({
                  ...generation.result,
                  generationId: pricingGenerationId,
                  propertyKey: generation.propertyKey,
                  // Not persisted inside result.generatedAt (only ever set
                  // client-side for a fresh, same-session generation) - the
                  // generation doc's own createdAt is the authoritative
                  // timestamp for a reopened/previously-generated result.
                  generatedAt: generation.createdAt,
                });
              })
              .catch(() => {
                if (!cancelled) setExistingPricingResult(null);
              });
          } else {
            setExistingPricingResult(null);
          }

          // Listing Description tracks a selected version separately from
          // the latest generation (spec section 52's "Selected Version" vs
          // "Most Recent Version") - the dashboard/modal always opens the
          // SELECTED one, falling back to the plain generationId for safety.
          const listingDescriptionEntry = data?.existingStrategies?.listingDescription;
          const listingDescriptionGenerationId = listingDescriptionEntry?.selectedGenerationId || listingDescriptionEntry?.generationId;
          if (listingDescriptionGenerationId) {
            functionGetStrategyGenerationResult({ transactionId: transaction.id, generationId: listingDescriptionGenerationId })
              .then((generation) => {
                if (cancelled || !generation) return;
                setExistingListingDescriptionResult({
                  ...generation.result,
                  userEdits: generation.userEdits || null,
                  generationId: listingDescriptionGenerationId,
                  generationNumber: generation.generationNumber,
                  propertyKey: generation.propertyKey,
                  generatedAt: generation.createdAt,
                });
              })
              .catch(() => {
                if (!cancelled) setExistingListingDescriptionResult(null);
              });
          } else {
            setExistingListingDescriptionResult(null);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setEntitlement(null);
          setExistingPricingResult(null);
          setExistingListingDescriptionResult(null);
        })
        .finally(() => {
          if (!cancelled) setIsLoadingEntitlement(false);
        });
    } else {
      setEntitlement(null);
      setExistingPricingResult(null);
      setExistingListingDescriptionResult(null);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id, addressKey, entitlementRefresh]);

  // Called by StrategyPricingModal right after a successful generate/
  // regenerate, so the dashboard's own state reflects it immediately -
  // without this, closing the modal and reopening it (before this
  // component's own fetch effect happened to rerun) would reopen with
  // stale/empty existingPricingResult and the modal would start over from
  // scratch instead of showing what was just generated and saved.
  function handleStrategyGenerated({ generationId, propertyKey: generatedPropertyKey, result }) {
    setExistingPricingResult({ ...result, generationId, propertyKey: generatedPropertyKey });
    setEntitlement((prev) => ({
      ...(prev || {}),
      isPropertyUnlocked: true,
      existingStrategies: {
        ...(prev?.existingStrategies || {}),
        pricing: {
          generationId,
          generatedAt: new Date(),
          summary: { recommendedListPrice: result?.recommendedListPrice ?? null },
        },
      },
    }));
    setPropertyGroupsRefreshSignal((n) => n + 1);
  }

  // justEnteredPropertyDetails: passed straight through from
  // StrategyCreditConfirmModal's "Use 1 Credit & Unlock Strategies" click
  // (via onConfirmed) when it's the one opening this - the transaction prop
  // here comes from Redux, whose live listener can still be catching up to
  // that same-moment Firestore write, so this is what actually guarantees
  // the fields show up correctly the instant StrategyPricingModal opens.
  function openPricingModal(justEnteredPropertyDetails) {
    dispatch(
      openModal({
        modalType: "StrategyPricingModal",
        modalProps: { transaction, existingPricingResult, onGenerated: handleStrategyGenerated, justEnteredPropertyDetails },
      })
    );
  }

  // Accepts an optional freshly-fetched entitlement so the post-Checkout
  // fulfillment handler below can resume the flow with the just-updated
  // credit balance, instead of whatever `entitlement` state was as of the
  // page's last render (which could still reflect zero credits if state
  // hasn't re-rendered yet).
  function handleOpenPricingStrategy(entitlementOverride) {
    const activeEntitlement = entitlementOverride || entitlement;
    if (!transaction?.id || !activeEntitlement) return;

    if (isUnsupportedPropertyType) {
      toast.error(UNSUPPORTED_PROPERTY_TYPE_MESSAGE);
      return;
    }

    if (!activeEntitlement.accountInGoodStanding) {
      toast.error("Intelligent Strategies are unavailable while your TransActioner account is past due.");
      return;
    }

    if (activeEntitlement.isPropertyUnlocked) {
      openPricingModal();
      return;
    }

    dispatch(
      openModal({
        modalType: "StrategyCreditConfirmModal",
        modalProps: {
          transactionId: transaction.id,
          propertyAddress: transaction.address,
          propertyPic: transaction.pic,
          propertyDetails: transaction.propertyDetails,
          triggerStrategyType: "pricing",
          entitlement: activeEntitlement,
          onConfirmed: openPricingModal,
        },
      })
    );
  }

  // Mirrors handleStrategyGenerated above - updates local state immediately
  // after a successful generate/regenerate so closing and reopening the
  // modal doesn't lose what was just created before this component's own
  // fetch effect happens to rerun.
  function handleListingDescriptionGenerated({ generationId, propertyKey: generatedPropertyKey, generationNumber, result }) {
    setExistingListingDescriptionResult({ ...result, generationId, propertyKey: generatedPropertyKey, generationNumber });
    setEntitlement((prev) => ({
      ...(prev || {}),
      isPropertyUnlocked: true,
      existingStrategies: {
        ...(prev?.existingStrategies || {}),
        listingDescription: {
          generationId,
          latestGenerationId: generationId,
          selectedGenerationId: generationId,
          generationNumber,
          generationCount: generationNumber,
          generatedAt: new Date(),
          summary: { title: result?.title ?? null },
        },
      },
    }));
    setPropertyGroupsRefreshSignal((n) => n + 1);
  }

  // Selecting an older version (spec section 52) never consumes a
  // regeneration and never touches generationCount - only which version is
  // "in use" changes.
  function handleListingDescriptionVersionSelected({ generationId, propertyKey: selectedPropertyKey, result }) {
    setExistingListingDescriptionResult({ ...result, generationId, propertyKey: selectedPropertyKey });
    setEntitlement((prev) => ({
      ...(prev || {}),
      existingStrategies: {
        ...(prev?.existingStrategies || {}),
        listingDescription: {
          ...(prev?.existingStrategies?.listingDescription || {}),
          selectedGenerationId: generationId,
        },
      },
    }));
  }

  function openListingDescriptionModal(justEnteredPropertyDetails) {
    dispatch(
      openModal({
        modalType: "StrategyListingDescriptionModal",
        modalProps: {
          transaction,
          existingListingDescriptionResult,
          onGenerated: handleListingDescriptionGenerated,
          onVersionSelected: handleListingDescriptionVersionSelected,
          justEnteredPropertyDetails,
        },
      })
    );
  }

  function handleOpenListingDescriptionStrategy(entitlementOverride) {
    const activeEntitlement = entitlementOverride || entitlement;
    if (!transaction?.id || !activeEntitlement) return;

    if (isUnsupportedPropertyType) {
      toast.error(UNSUPPORTED_PROPERTY_TYPE_MESSAGE);
      return;
    }

    if (!activeEntitlement.accountInGoodStanding) {
      toast.error("Intelligent Strategies are unavailable while your TransActioner account is past due.");
      return;
    }

    if (activeEntitlement.isPropertyUnlocked) {
      openListingDescriptionModal();
      return;
    }

    dispatch(
      openModal({
        modalType: "StrategyCreditConfirmModal",
        modalProps: {
          transactionId: transaction.id,
          propertyAddress: transaction.address,
          propertyPic: transaction.pic,
          propertyDetails: transaction.propertyDetails,
          triggerStrategyType: "listingDescription",
          entitlement: activeEntitlement,
          onConfirmed: openListingDescriptionModal,
        },
      })
    );
  }

  // Property Intelligence: one saved report per property. If research exists
  // (in any state) the report opens; otherwise the intake/unlock modal does.
  const propertyIntelligencePointer = entitlement?.existingStrategies?.propertyIntelligence;
  const hasPropertyIntelligence = Boolean(propertyIntelligencePointer) && propertyIntelligencePointer.status !== "reset";

  function openPropertyIntelligenceModal(propertyKey) {
    dispatch(
      openModal({
        modalType: "PropertyIntelligenceModal",
        modalProps: { transaction, propertyKey, onChanged: () => setEntitlementRefresh((n) => n + 1) },
      })
    );
  }

  function handleOpenPropertyIntelligence(propertyKey) {
    if (!transaction?.id || !entitlement) return;
    if (propertyKey || hasPropertyIntelligence) {
      openPropertyIntelligenceModal(propertyKey);
      return;
    }
    if (isUnsupportedPropertyType) {
      toast.error(UNSUPPORTED_PROPERTY_TYPE_MESSAGE);
      return;
    }
    if (!entitlement.accountInGoodStanding) {
      toast.error("Intelligent Strategies are unavailable while your TransActioner account is past due.");
      return;
    }
    dispatch(
      openModal({
        modalType: "PropertyIntelligenceUnlockModal",
        modalProps: {
          transaction,
          entitlement,
          onStarted: () => {
            setEntitlementRefresh((n) => n + 1);
            setPropertyGroupsRefreshSignal((n) => n + 1);
            openPropertyIntelligenceModal();
          },
        },
      })
    );
  }

  function toDisplayDate(value) {
    if (!value) return null;
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy");
  }

  const isPastDue = entitlement && !entitlement.accountInGoodStanding;
  const buttonDisabled = isLoadingEntitlement || isPastDue || isUnsupportedPropertyType;
  // isUnsupportedPropertyType is checked first - a Land/Foreclosure/
  // Commercial transaction stays restricted regardless of account standing.
  const restrictedMessage = isUnsupportedPropertyType
    ? UNSUPPORTED_PROPERTY_TYPE_MESSAGE
    : "Intelligent Strategies are unavailable while your TransActioner account is past due.";

  // Compact property summary bar (Seller only) - same field names/formula
  // StrategyPricingModal.jsx's buildDefaultInputs already uses for these
  // exact fields (bedroomsTotal, bathroomsFull+half*0.5+threeQuarter*0.75,
  // aboveGradeFinishedSqft, propertyType, salesPrice), so the numbers shown
  // here always match what a freshly generated Strategy would use.
  const sellerPropertyDetails = transaction?.propertyDetails || {};
  const sellerBathrooms = sellerPropertyDetails.bathroomsFull
    ? sellerPropertyDetails.bathroomsFull +
      (sellerPropertyDetails.bathroomsHalf || 0) * 0.5 +
      (sellerPropertyDetails.bathroomsThreeQuarter || 0) * 0.75
    : null;
  const sellerPropertyFacts = [
    sellerPropertyDetails.bedroomsTotal && `${sellerPropertyDetails.bedroomsTotal} Beds`,
    sellerBathrooms && `${sellerBathrooms} Baths`,
    sellerPropertyDetails.aboveGradeFinishedSqft && `${Number(sellerPropertyDetails.aboveGradeFinishedSqft).toLocaleString()} Sq Ft`,
    sellerPropertyDetails.propertyType,
  ].filter(Boolean).join(" | ");
  const sellerAddressLine = transaction?.address ? convertAddressFull(transaction.address) : null;
  const sellerCurrentListPrice = transaction?.salesPrice ? `$${transaction.salesPrice}` : null;

  return (
    <div className="secondary-page-wrapper">
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Buyer renders its own page header (title + Compare Homes/Add
          Property actions) inside BuyerStrategyPropertyRows - rendering a
          second one here would duplicate it. */}
      {!isBuyer && <StrategyPageHeader title="Seller Strategies" isMobile={isMobile} />}
      {/* The Buyer page explains Property Intelligence on each property card instead. */}
      {!isBuyer && (
        <p className="small top margin text-grey">
          {isStrategyVisible("pricing")
            ? "TransActioner researches available property information automatically. Review the details and use Edit Details & Regenerate when additional context is needed."
            : "Property Intelligence researches each property once and saves the report to this transaction for you to review, edit and share."}
        </p>
      )}
      {isUnsupportedPropertyType ? (
        <p className="small top margin text red">{UNSUPPORTED_PROPERTY_TYPE_MESSAGE}</p>
      ) : (
        isPastDue && (
          <p className="small top margin text red">
            Intelligent Strategies are unavailable while your TransActioner account is past due.
          </p>
        )
      )}

      {isBuyer ? (
        <BuyerStrategyPropertyRows
          transaction={transaction}
          entitlement={entitlement}
          existingPricingResult={existingPricingResult}
          onOpenPricingStrategy={handleOpenPricingStrategy}
          onOpenPropertyIntelligence={handleOpenPropertyIntelligence}
          propertyIntelligencePointer={hasPropertyIntelligence ? propertyIntelligencePointer : null}
          strategiesRestricted={buttonDisabled}
          strategiesRestrictedMessage={restrictedMessage}
          onGenerated={handleStrategyGenerated}
          refreshSignal={propertyGroupsRefreshSignal}
          onRefreshNeeded={() => setPropertyGroupsRefreshSignal((n) => n + 1)}
        />
      ) : (
        <>
          {sellerAddressLine && (
            <div style={{ marginTop: "20px" }}>
              <StrategyPropertyDashboard
                minCardWidth={230}
                showUnlockedBanner={Boolean(entitlement?.isPropertyUnlocked)}
                propertyHeaderProps={{
                  pic: transaction?.pic || null,
                  street: transaction?.address?.street,
                  unit: transaction?.address?.unit,
                  city: transaction?.address?.city,
                  state: transaction?.address?.state,
                  zipcode: transaction?.address?.zipcode,
                  facts: sellerPropertyFacts ? sellerPropertyFacts.split(" | ") : [],
                  currentPrice: sellerCurrentListPrice,
                  isMobile,
                }}
              >
                {isStrategyVisible("propertyIntelligence") && (
                  <StrategyCard
                    {...buildPropertyIntelligenceCard({
                      pointer: hasPropertyIntelligence ? propertyIntelligencePointer : null,
                      isCurrent: true,
                      restricted: buttonDisabled && !hasPropertyIntelligence,
                      restrictedMessage,
                      onAction: () => handleOpenPropertyIntelligence(),
                    })}
                    lastUpdated={toDisplayDate(propertyIntelligencePointer?.completedAt)}
                  />
                )}

                {isStrategyVisible("pricing") && (
                <StrategyCard
                  theme={STRATEGY_THEME.pricing}
                  description="Market value and recommended listing approach."
                  status={buttonDisabled ? "restricted" : hasPricingStrategy ? "generated" : "not_generated"}
                  restrictedMessage={restrictedMessage}
                  preview={
                    hasPricingStrategy && (existingPricingResult?.recommendedListPrice || pricingSummary?.recommendedListPrice) ? (
                      <PricingResultPreview
                        label="Recommended list price"
                        recommendedPrice={existingPricingResult?.recommendedListPrice || pricingSummary?.recommendedListPrice}
                        estimatedValueLow={existingPricingResult?.estimatedValueLow}
                        estimatedValueHigh={existingPricingResult?.estimatedValueHigh}
                      />
                    ) : null
                  }
                  lastUpdated={toDisplayDate(pricingGeneratedAt)}
                  onAction={() => handleOpenPricingStrategy()}
                />
                )}

                {isStrategyVisible("listingDescription") && (
                <StrategyCard
                  theme={STRATEGY_THEME.listingDescription}
                  description="Create compelling MLS descriptions and social media copy."
                  status={buttonDisabled ? "restricted" : hasListingDescriptionStrategy ? "generated" : "not_generated"}
                  restrictedMessage={restrictedMessage}
                  preview={
                    hasListingDescriptionStrategy ? (
                      <ListingDescriptionResultPreview
                        writingStyle={existingListingDescriptionResult?.writingStyle}
                        previewText={truncate(existingListingDescriptionResult?.mlsDescription) || listingDescriptionSummary?.title}
                      />
                    ) : null
                  }
                  features={["MLS-ready descriptions", "Different writing styles", "Social media copy"]}
                  footnote={!hasListingDescriptionStrategy ? "Included with this property's unlock" : null}
                  actionLabel={hasListingDescriptionStrategy ? "View Description" : "Generate Description"}
                  lastUpdated={toDisplayDate(listingDescriptionGeneratedAt)}
                  onAction={() => handleOpenListingDescriptionStrategy()}
                />
                )}

                {/* Matches the Buyer page's Negotiations Strategy card exactly
                    (same theme, same text) - no implementation exists behind
                    it yet for either Buyer or Seller. */}
                {isStrategyVisible("negotiation") && (
                  <StrategyCard
                    theme={STRATEGY_THEME.negotiation}
                    description="Negotiating position, options and contract terms."
                    comingSoonDetail="Get AI guidance on key negotiation points and potential terms."
                    status="coming_soon"
                  />
                )}
              </StrategyPropertyDashboard>
            </div>
          )}

          <TransactionStrategyPropertyGroups transaction={transaction} refreshSignal={propertyGroupsRefreshSignal} />
        </>
      )}
      </div>
    </div>
  );
}
